/**
 * @fileoverview Detail Event Table
 * @description Row-level results of a group detail report: every evaluated event with its
 * source text, per-dimension score, verdict, suggested label, rationale, and evidence.
 * @module features/ScoutEval/components/DetailEventTable
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { ScoreChip } from './common';
import { BAND_COLORS } from '../constants';

/**
 * Flatten every searchable field of a row.
 * @param {Object} row - Parsed event row.
 * @returns {string} Lowercase haystack.
 */
function searchIndex(row) {
  return [
    row.eventId,
    row.title,
    row.sourceId,
    ...row.source.map((section) => `${section.heading} ${section.body}`),
    ...row.cells.flatMap((cell) => [
      cell.column,
      cell.label,
      cell.verdict,
      cell.suggested,
      ...cell.notes.map((note) => `${note.label} ${note.body}`),
    ]),
  ].join(' ').toLowerCase();
}

/**
 * Render one scored dimension.
 * @param {Object} props - Dimension result.
 * @returns {JSX.Element} Dimension cell.
 */
function DimensionCell({ cell }) {
  return (
    <Stack spacing={0.75} className="scout-dimension">
      {cell.label && <Typography className="scout-dimension-label">{cell.label}</Typography>}
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        {cell.value && <ScoreChip score={cell.score} band={cell.band} label={cell.value} />}
        {cell.verdict && (
          <Chip
            size="small"
            className="scout-verdict"
            label={cell.verdict}
            sx={{
              color: BAND_COLORS[cell.verdictTone],
              backgroundColor: `${BAND_COLORS[cell.verdictTone]}16`,
            }}
          />
        )}
      </Stack>
      {cell.suggested && (
        <Typography className="scout-dimension-suggested">
          <strong>Suggested:</strong> {cell.suggested}
        </Typography>
      )}
      {cell.notes.map((note) => (
        <Box key={`${note.label}-${note.body.slice(0, 24)}`} className="scout-note">
          <Typography className="scout-note-label">{note.label}</Typography>
          <Typography className="scout-note-body">{note.body}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * Render one evaluated event.
 * @param {Object} props - Parsed row and its column headings.
 * @returns {JSX.Element} Event row.
 */
function EventRow({ row, columnCount }) {
  const [expanded, setExpanded] = useState(false);
  const hasSource = row.source.length > 0;

  return (
    <>
      <TableRow hover className="scout-event-row">
        <TableCell className="scout-event-cell">
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            {hasSource && (
              <IconButton
                size="small"
                className="scout-event-toggle"
                aria-label={expanded ? `Hide source text for ${row.eventId}` : `Show source text for ${row.eventId}`}
                aria-expanded={expanded}
                onClick={() => setExpanded((open) => !open)}
              >
                {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              </IconButton>
            )}
            <Box minWidth={0}>
              <Typography className="scout-event-id">{row.eventId}</Typography>
              <Typography className="scout-event-title">{row.title}</Typography>
              {row.sourceId && <Typography className="scout-event-source-id">{row.sourceId}</Typography>}
            </Box>
          </Stack>
        </TableCell>
        {row.cells.map((cell) => (
          <TableCell key={cell.column} className="scout-dimension-cell">
            <DimensionCell cell={cell} />
          </TableCell>
        ))}
      </TableRow>
      {hasSource && (
        <TableRow className="scout-source-row">
          <TableCell colSpan={columnCount} className="scout-source-cell">
            <Collapse in={expanded} unmountOnExit>
              <Box className="scout-source">
                {row.source.map((section) => (
                  <Box key={`${section.heading}-${section.body.slice(0, 24)}`} className="scout-source-section">
                    <Typography className="scout-note-label">{section.heading}</Typography>
                    <Typography className="scout-source-body">{section.body}</Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Render the full row-level results of a group detail report.
 * @param {Object} props - Parsed row layout.
 * @returns {JSX.Element} Event table.
 */
export function DetailEventTable({ content }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('all');

  const indexed = useMemo(
    () => content.rows.map((row) => ({ row, haystack: searchIndex(row) })),
    [content.rows],
  );

  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return indexed
      .filter(({ row, haystack }) => (
        (verdictFilter === 'all' || row.verdicts.includes(verdictFilter)) && haystack.includes(term)
      ))
      .map(({ row }) => row);
  }, [indexed, searchTerm, verdictFilter]);

  const verdictOptions = content.verdictOptions || [];

  return (
    <>
      <Box className="scout-detail-controls">
        <TextField
          size="small"
          className="scout-detail-search"
          placeholder={content.searchPlaceholder}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': 'Search detail rows' },
            input: { startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> },
          }}
        />
        {verdictOptions.length > 0 && (
          <Select
            size="small"
            value={verdictFilter}
            onChange={(event) => setVerdictFilter(event.target.value)}
            inputProps={{ 'aria-label': 'Filter by verdict' }}
          >
            {verdictOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        )}
        <Typography className="scout-toolbar-count">
          {visibleRows.length} of {content.rows.length} rows shown
        </Typography>
      </Box>

      <TableContainer className="scout-detail-table-container">
        <Table size="small" className="scout-table scout-detail-table">
          <TableHead>
            <TableRow>
              {content.columns.map((column) => <TableCell key={column}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <EventRow key={row.key} row={row} columnCount={content.columns.length} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {visibleRows.length === 0 && (
        <Box className="scout-no-results">
          <Typography color="text.secondary">No rows match the current search and verdict filter.</Typography>
        </Box>
      )}
    </>
  );
}

export default DetailEventTable;
